import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Drawer from '@mui/material/Drawer';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import { useVisibleNav } from '@/entities/session';
import { radius } from '@/shared/theme';
import { Icon, type IconName, Logo } from '@/shared/ui';
import { useSidebarStore } from '../model/sidebar-store';

export const SIDEBAR_WIDTH = 268;
export const SIDEBAR_MINI_WIDTH = 82;

interface AppSidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function AppSidebar({ mobileOpen, onMobileClose }: AppSidebarProps) {
  const mini = useSidebarStore((s) => s.mini);

  return (
    <>
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flex: 'none',
          height: '100dvh',
          width: mini ? SIDEBAR_MINI_WIDTH : SIDEBAR_WIDTH,
          transition: 'width .24s ease',
        }}
      >
        <SidebarPanel mini={mini} />
      </Box>
      <Drawer
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { lg: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 0, background: 'transparent' },
        }}
      >
        <SidebarPanel mini={false} onNavigate={onMobileClose} />
      </Drawer>
    </>
  );
}

function SidebarPanel({ mini, onNavigate }: { mini: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const groups = useVisibleNav();
  const toggleMini = useSidebarStore((s) => s.toggleMini);
  const { pathname } = useLocation();

  // Nested routes (`/bot-configuration/script-runner`) match their parent item's prefix too, so
  // only the most specific matching item is highlighted.
  const activePath = groups
    .flatMap((group) => group.items.map((item) => item.path))
    .filter((path) => pathname === path || pathname.startsWith(`${path}/`))
    .sort((x, y) => y.length - x.length)[0];

  return (
    <Box
      sx={(theme) => ({
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        color: '#FFFFFF',
        background: theme.vars.palette.md.gradSidebar,
      })}
    >
      <Box
        sx={{
          height: 74,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          borderBottom: '1px solid rgba(255,255,255,.1)',
        }}
      >
        {mini ? (
          <Logo variant="mini" />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px', minWidth: 0 }}>
            <Box sx={{ alignSelf: 'flex-start' }}>
              <Logo variant="plaque" />
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 9.5,
                letterSpacing: '.16em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,.5)',
                pl: '2px',
              }}
            >
              {t('app.orgTag')}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        component="nav"
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: '12px 12px 20px',
          // A faint thumb on a transparent track, so the scrollbar sits in the sidebar's blue
          // instead of showing the browser's light grey track.
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,.22) transparent',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,.22)',
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(255,255,255,.36)' },
        }}
      >
        {groups.map((group) => (
          <Box key={group.key}>
            {mini ? (
              <Box sx={{ height: '1px', background: 'rgba(255,255,255,.1)', m: '9px 8px' }} />
            ) : (
              <Box
                sx={{
                  p: '15px 12px 7px',
                  fontSize: 9.5,
                  letterSpacing: '.15em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,.4)',
                  fontWeight: 700,
                }}
              >
                {t(`nav.groups.${group.key}`)}
              </Box>
            )}
            {group.items.map((item) => (
              <SidebarLink
                key={item.key}
                to={item.path}
                icon={item.icon}
                label={t(`nav.items.${item.key}`)}
                mini={mini}
                active={item.path === activePath}
                onClick={onNavigate}
              />
            ))}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          flex: 'none',
          display: { xs: 'none', lg: 'block' },
          borderTop: '1px solid rgba(255,255,255,.1)',
          p: '10px 12px',
        }}
      >
        <ButtonBase
          onClick={toggleMini}
          aria-label={mini ? t('nav.expand') : t('nav.collapse')}
          sx={navButtonSx(mini, false, { color: 'rgba(255,255,255,.6)', fontSize: 13 })}
        >
          <Box component="span" sx={iconSlotSx}>
            <Icon name={mini ? 'chevronsR' : 'chevronsL'} />
          </Box>
          {!mini && <span>{t('nav.collapse')}</span>}
        </ButtonBase>
      </Box>
    </Box>
  );
}

interface SidebarLinkProps {
  to: string;
  icon: IconName;
  label: string;
  mini: boolean;
  active: boolean;
  onClick?: () => void;
}

function SidebarLink({ to, icon, label, mini, active, onClick }: SidebarLinkProps) {
  return (
    <ButtonBase
      component={NavLink}
      to={to}
      title={label}
      aria-current={active ? 'page' : undefined}
      onClick={onClick}
      sx={navButtonSx(mini, active)}
    >
      <Box
        component="span"
        sx={[iconSlotSx, { color: active ? '#63BAEA' : 'rgba(255,255,255,.62)' }]}
      >
        <Icon name={icon} />
      </Box>
      {!mini && (
        <Box
          component="span"
          sx={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {label}
        </Box>
      )}
    </ButtonBase>
  );
}

const iconSlotSx = {
  display: 'flex',
  flex: 'none',
  width: 20,
  height: 20,
  alignItems: 'center',
  justifyContent: 'center',
} as const;

function navButtonSx(mini: boolean, active: boolean, extra: object = {}) {
  return {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: mini ? 'center' : 'flex-start',
    gap: '12px',
    p: mini ? '10px 0' : '10px 12px',
    mb: '2px',
    borderRadius: radius.control,
    textAlign: 'left',
    fontSize: 14,
    fontWeight: active ? 700 : 600,
    color: active ? '#FFFFFF' : 'rgba(255,255,255,.74)',
    background: active ? 'rgba(255,255,255,.16)' : 'transparent',
    '&:hover': {
      background: active ? 'rgba(255,255,255,.16)' : 'rgba(255,255,255,.09)',
      color: '#FFFFFF',
    },
    '&.Mui-focusVisible': { outline: '2px solid #CFE45E', outlineOffset: -2 },
    ...extra,
  } as const;
}
